import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChapterEmblem } from "@/lib/brand";
import LockedChapter from "@/components/LockedChapter";
import ProductImageTile from "@/components/ProductImageTile";
import WaitlistButton from "@/components/WaitlistButton";
import PageMeta from "@/components/PageMeta";
import { Lock } from "lucide-react";

export default function ChapterDetail() {
  const { roman } = useParams();
  const [data, setData] = useState(null);
  const [gender, setGender] = useState("all");   // all | men | women
  const [subCat, setSubCat] = useState("all");   // "all" or product name (canonical)

  useEffect(() => {
    setData(null);
    setGender("all");
    setSubCat("all");
    api.get(`/chapters/${roman}`).then(r => setData(r.data)).catch(() => setData({ error: true }));
  }, [roman]);

  if (!data) return <div className="pt-40 text-center text-ink/50 tracking-widest-plus text-xs uppercase">Loading…</div>;
  if (data.error) return <div className="pt-40 text-center">Chapter not found</div>;

  const { chapter, products } = data;

  // Unrevealed / non-public chapter safety fallback (should not occur — I..XI are all revealed)
  if (!chapter.revealed) {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ background: "#f4ecd8" }}>
        <div className="absolute inset-0">
          <LockedChapter roman={chapter.roman} showLabel={false} />
        </div>
        <div className="relative text-center px-8 max-w-2xl z-10 text-[#3a2916]">
          <Lock className="w-6 h-6 mx-auto text-ink/40 mb-8" strokeWidth={1} />
          <p className="text-[10px] tracking-luxury uppercase text-ink/45 mb-6">Chapter</p>
          <h1 className="font-editorial text-8xl md:text-[12rem] leading-none text-ink/50">{chapter.roman}</h1>
          <div className="rule-gold w-24 mx-auto my-10 opacity-60" />
          <p className="tracking-widest-plus text-[11px] uppercase text-ink/55">Reveal pending</p>
          <p className="mt-6 text-ink/55 leading-relaxed max-w-lg mx-auto font-editorial-italic text-xl">
            This chapter has not yet been revealed. The house will open it when the moment is right.
          </p>
          <Link to="/chapters" className="mt-12 inline-block tracking-widest-plus text-[11px] uppercase text-ink/60 hover:text-goldDark link-draw">Return to the chapters →</Link>
        </div>
      </div>
    );
  }

  // Revealed with photography → editorial male/female split hero
  // Revealed with products only → hero using product artwork
  // Revealed WITHOUT anything → cream editorial hero with typography only (no invented imagery)
  const hasFaces = !!(chapter.male_face && chapter.female_face);
  const hasProducts = products.length > 0;
  const hasHeroArt = hasFaces || hasProducts;
  return (
    <div className="bg-ivory text-ink">
      <PageMeta
        title={`Chapter ${chapter.roman} — ${chapter.name}`}
        description={`Chapter ${chapter.roman} · ${chapter.name} — ${chapter.tagline || 'a study of ' + chapter.name.toLowerCase()}. Explore the LOOXS ${chapter.name.toLowerCase()} chapter, designed in London and crafted in India, with pieces cut for both Him and Her.`}
        image={chapter.male_face ? `https://www.looxsfashion.com${chapter.male_face}` : undefined}
      />
      <section className="relative h-screen overflow-hidden">
        {hasFaces ? (
          <div className="absolute inset-0 grid grid-cols-2">
            <img src={chapter.male_face} alt="" className="w-full h-full object-cover animate-slow-zoom" />
            <img src={chapter.female_face} alt="" className="w-full h-full object-cover animate-slow-zoom" />
          </div>
        ) : hasProducts ? (
          <img src={products[0].images?.[0]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-95 animate-slow-zoom" />
        ) : (
          <>
            <div className="absolute inset-0" style={{
              background: "radial-gradient(ellipse at 50% 40%, #f5ecd8 0%, #ead9b6 55%, #d9c39a 100%)"
            }} />
            <div className="absolute inset-0" style={{
              background: "linear-gradient(120deg, transparent 30%, rgba(201,169,97,0.14) 45%, transparent 60%)",
            }} />
          </>
        )}
        {hasHeroArt && <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(43,31,20,0.35) 0%, rgba(43,31,20,0.05) 45%, rgba(43,31,20,0.45) 100%)" }} />}
        <div className="grain opacity-40" />
        <div className={`relative h-full flex flex-col items-center justify-center text-center px-8 pt-24 ${hasHeroArt ? "text-ivory" : "text-ink"}`}>
          <p className={`text-[10px] tracking-luxury uppercase mb-8 animate-fade-up ${hasHeroArt ? "text-gold" : "text-goldDark"}`}>Chapter</p>
          <ChapterEmblem roman={chapter.roman} size={80} stroke={hasHeroArt ? "#f4ecd8" : "#8b6f3d"} />
          <h1 data-testid="chapter-roman" className="font-editorial text-[22vw] md:text-[16vw] leading-[0.9] mt-6 animate-fade-up" style={{ animationDelay: "200ms" }}>{chapter.roman}</h1>
          <p data-testid="chapter-name" className={`font-editorial-italic text-3xl md:text-5xl mt-4 animate-fade-up ${hasHeroArt ? "text-ivory/90" : "text-ink/80"}`} style={{ animationDelay: "400ms" }}>{chapter.name}</p>
          {chapter.tagline && <p className={`mt-8 tracking-widest-plus text-[11px] uppercase animate-fade-up ${hasHeroArt ? "text-ivory/70" : "text-ink/55"}`} style={{ animationDelay: "600ms" }}>{chapter.tagline}</p>}
        </div>
      </section>

      <section className="py-24 md:py-32 text-ink" style={{ background: "#e5dbc7" }}>
        <div className="max-w-3xl mx-auto text-center px-8">
          <p className="text-[10px] tracking-luxury uppercase text-goldDark mb-6">Material language</p>
          <p className="font-editorial text-3xl md:text-5xl leading-tight">{chapter.material_language || "—"}</p>
        </div>
      </section>

      <section className="bg-ivory text-ink py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
            <div>
              <p className="text-[10px] tracking-luxury uppercase text-goldDark mb-4">In this chapter</p>
              <h2 className="font-editorial text-4xl md:text-6xl tracking-tight">Objects</h2>
            </div>
            <Link to="/chapters" className="tracking-widest-plus text-[11px] uppercase link-draw">All chapters →</Link>
          </div>
          {(() => {
            // Filter chips derived from the loaded product list. The category label is
            // the piece name minus its " — Him"/" — Her" suffix so we can toggle by garment.
            const rootName = (p) => (p.name || "").replace(/\s+—\s+(Him|Her)\s*$/i, "").trim();
            const subCategories = Array.from(new Set(products.map(rootName)));
            const filtered = products.filter(p =>
              (gender === "all" || p.gender === gender) &&
              (subCat === "all" || rootName(p) === subCat)
            );
            const chip = (active, label, testid, onClick) => (
              <button
                data-testid={testid}
                onClick={onClick}
                className={`px-4 py-2 text-[10px] tracking-widest-plus uppercase border transition-colors duration-300 ${active ? "bg-ink text-ivory border-ink" : "bg-transparent text-ink/70 border-ink/25 hover:border-ink hover:text-ink"}`}
              >{label}</button>
            );
            return (
              <>
                {/* Gender toggle */}
                <div data-testid="filter-gender" className="flex flex-wrap gap-2 mb-4">
                  {chip(gender==="all",   "All",  "filter-gender-all",   () => setGender("all"))}
                  {chip(gender==="men",   "Him",  "filter-gender-men",   () => setGender("men"))}
                  {chip(gender==="women", "Her",  "filter-gender-women", () => setGender("women"))}
                </div>
                {/* Sub-category chips */}
                <div data-testid="filter-subcategory" className="flex flex-wrap gap-2 mb-12">
                  {chip(subCat==="all", "All pieces", "filter-sub-all", () => setSubCat("all"))}
                  {subCategories.map(name => chip(
                    subCat===name, name, `filter-sub-${name.toLowerCase().replace(/\s+/g,"-")}`,
                    () => setSubCat(name)
                  ))}
                </div>

                {filtered.length === 0 ? (
                  <p className="font-editorial-italic text-2xl text-ink/50 py-16 text-center">No pieces match this selection.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-16">
                    {filtered.map((p, i) => (
                      <div key={p.id} className="group animate-fade-up relative" style={{ animationDelay: `${i * 60}ms` }}>
                        <Link to={`/product/${p.slug}`} data-testid={`chapter-product-${p.slug}`} className="block">
                          <ProductImageTile src={p.images[0]} hoverSrcs={p.images.slice(1)} alt={p.name} />
                          <div className="mt-6 flex items-start justify-between gap-4">
                            <div>
                              <p className="font-editorial text-xl leading-tight">{p.name}</p>
                              <p className="text-[10px] tracking-widest-plus uppercase text-black/50 mt-2">
                                {p.availability === "in_store" ? "In store" : p.availability === "pre_order" ? "Pre-order" : "Coming soon"}
                              </p>
                            </div>
                          </div>
                        </Link>
                        <WaitlistButton product={p} />
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </section>
    </div>
  );
}
